import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import { cn } from "../lib/utils";

const statusColor = {
  planned: "bg-slate-400", in_progress: "bg-primary", review: "bg-amber-500", completed: "bg-green-500", cancelled: "bg-slate-300",
};

export default function AuditCalendarPage() {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const start = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1).toISOString();
    const end = new Date(cursor.getFullYear(), cursor.getMonth() + 2, 0).toISOString();
    try {
      const { data } = await api.get(`/audits/calendar?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`);
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [cursor]);
  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const map = {};
    items.forEach((a) => {
      const dates = [a.start_date, a.due_date].filter(Boolean);
      dates.forEach((iso) => {
        const d = new Date(iso);
        const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
        (map[key] = map[key] || []).push({ ...a, _dateKind: iso === a.start_date ? "start" : "due" });
      });
    });
    return map;
  }, [items]);

  const monthStart = new Date(cursor);
  const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const startWeekday = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();
  const today = new Date();

  const grid = [];
  for (let i = 0; i < startWeekday; i++) grid.push(null);
  for (let d = 1; d <= daysInMonth; d++) grid.push(d);
  while (grid.length % 7 !== 0) grid.push(null);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Audit Calendar</h1>
          <p className="text-sm text-muted-foreground mt-1">Month view of audits with planned start and due dates.</p>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="cal-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="h-9 min-w-[160px] px-3 flex items-center justify-center border border-border rounded-md text-sm font-medium">
            {cursor.toLocaleString(undefined, { month: "long", year: "numeric" })}
          </div>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="cal-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => { const d = new Date(); d.setDate(1); setCursor(d); }} data-testid="cal-today">Today</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-7 gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-2">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="text-center">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {grid.map((d, i) => {
              if (d === null) return <div key={`blank-${i}`} className="h-28 rounded-md border border-dashed border-border/60" />;
              const key = `${cursor.getFullYear()}-${cursor.getMonth()}-${d}`;
              const events = byDay[key] || [];
              const isToday =
                today.getFullYear() === cursor.getFullYear() &&
                today.getMonth() === cursor.getMonth() &&
                today.getDate() === d;
              return (
                <div
                  key={key}
                  className={cn(
                    "h-28 rounded-md border p-2 flex flex-col gap-1 overflow-hidden",
                    isToday ? "border-primary bg-primary-subtle/30 dark:bg-primary/10" : "border-border"
                  )}
                >
                  <div className={cn("text-xs font-medium", isToday && "text-primary")}>{d}</div>
                  <div className="space-y-1 overflow-y-auto">
                    {events.slice(0, 3).map((e) => (
                      <button
                        key={`${e.id}-${e._dateKind}`}
                        data-testid={`cal-event-${e.id}-${e._dateKind}`}
                        onClick={() => navigate(`/audits/${e.id}`)}
                        className="w-full text-left text-[10px] rounded px-1.5 py-0.5 truncate hover:bg-muted transition-colors flex items-center gap-1"
                        title={`${e.title} (${e._dateKind})`}
                      >
                        <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", statusColor[e.status] || "bg-slate-400")} />
                        <span className="truncate">{e._dateKind === "start" ? "▶" : "⚑"} {e.title}</span>
                      </button>
                    ))}
                    {events.length > 3 && (
                      <div className="text-[10px] text-muted-foreground">+{events.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Legend</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-4 text-xs">
          <LegendDot color="bg-slate-400" label="Planned" />
          <LegendDot color="bg-primary" label="In progress" />
          <LegendDot color="bg-amber-500" label="Review" />
          <LegendDot color="bg-green-500" label="Completed" />
          <span className="text-muted-foreground">▶ Start · ⚑ Due</span>
        </CardContent>
      </Card>
    </div>
  );
}
function LegendDot({ color, label }) {
  return <div className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${color}`} /> {label}</div>;
}
